// High-level encode/decode: `EphemeralEnvelope` ↔ cleartext body.

import {
  decodeEphemeralBody,
  decodeEphemeralEnvelope,
  encodeEphemeralBody,
  encodeEphemeralEnvelope,
} from './cborEphemeral';
import { decryptEphemeralBody, encryptEphemeralBody } from './crypto';
import type { EphemeralKeyring } from './keyring';
import type { EphemeralBodyWire, EphemeralEnvelopeWire } from './cborEphemeral';
import type { EphemeralPayload, EphemeralType } from './types';

export interface EncodeEphemeralParams {
  readonly conversationId: Uint8Array;
  readonly senderDevice: Uint8Array;
  readonly type: EphemeralType;
  readonly payload: EphemeralPayload;
  readonly keyring: EphemeralKeyring;
}

export async function encodeEphemeralFrame(
  params: EncodeEphemeralParams,
): Promise<Uint8Array> {
  const { conversationId, senderDevice, type, payload, keyring } = params;
  const key = await keyring.deriveKey(conversationId);
  const { epoch, nonceSeq } = await keyring.nextNonceSeq(conversationId);
  const bodyBytes = encodeEphemeralBody({ type, payload } satisfies EphemeralBodyWire);
  const ciphertext = await encryptEphemeralBody(key, epoch, nonceSeq, bodyBytes);
  const wire: EphemeralEnvelopeWire = {
    conversation_id: conversationId,
    sender_device: senderDevice,
    epoch,
    nonce_seq: nonceSeq,
    ciphertext,
  };
  return encodeEphemeralEnvelope(wire);
}

export interface DecodedEphemeralFrame {
  readonly conversationId: Uint8Array;
  readonly senderDevice: Uint8Array;
  readonly epoch: number;
  readonly type: EphemeralType;
  readonly payload: EphemeralPayload;
}

export async function decodeEphemeralFrame(
  wire: Uint8Array,
  keyring: EphemeralKeyring,
): Promise<DecodedEphemeralFrame> {
  const env = decodeEphemeralEnvelope(wire);
  keyring.setEpoch(env.conversation_id, env.epoch);
  const key = await keyring.deriveKey(env.conversation_id);
  let plaintext: Uint8Array;
  try {
    plaintext = await decryptEphemeralBody(
      key,
      env.epoch,
      env.nonce_seq,
      env.ciphertext,
    );
  } catch {
    throw new Error('ephemeral decrypt failed');
  }
  const body = decodeEphemeralBody(plaintext);
  return {
    conversationId: env.conversation_id,
    senderDevice: env.sender_device,
    epoch: env.epoch,
    type: body.type as EphemeralType,
    payload: (body.payload ?? {}) as EphemeralPayload,
  };
}
